import { DatePipe, NgFor, NgIf } from '@angular/common'
import { Component } from '@angular/core'
import {
    FormArray,
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { DateAdapter, MatNativeDateModule } from '@angular/material/core'
import { MatDatepickerModule } from '@angular/material/datepicker'
import {
    MatError,
    MatFormField,
    MatFormFieldModule,
    MatLabel,
} from '@angular/material/form-field'
import { MatIcon, MatIconModule } from '@angular/material/icon'
import { MatInput, MatInputModule } from '@angular/material/input'
import { MatRadioModule } from '@angular/material/radio'
import { MatSelectModule } from '@angular/material/select'
import { MatStepperModule } from '@angular/material/stepper'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { SearchSelectComponent } from '../../../components/search-select/search-select.component'
import { Incident } from '../../../models/incident.interface'
import { IncidentService } from '../../../services/incident/incident.service'
import { ToastrService } from 'ngx-toastr'
import {
    civilStatus,
    disabilityTypes,
    educationLevel,
    ethnic,
    identificationTypes,
    insuranceKind,
    leadertype,
    lengueageProperty,
    occupation,
    typePerson,
    conflictRelated,
    sex,
    sexualOrientation,
    religion,
    eps,
    territories,
    organization,
    relationship,
    subjet,
    reportedTo,
    whoReports,
    sourceIncome,
    violence,
    affectationKindIncident,
} from './incident.definitions'
import { Outsider } from '../../../models/outsider.interface'
import { PersonService } from '../../../services/person/persona.service'
import { HttpErrorResponse } from '@angular/common/http'
import { ActivatedRoute, Router, RouterLink } from '@angular/router'
import { FormUtils } from '../../../utils'
import { SelectFileComponent } from '../../select-file/select-file.component'
import { MatDialog } from '@angular/material/dialog'
import { UploadfileService } from '../../../services/uploadfile/uploadfile.service'
import { Location } from '../../../models/location.interface'
import { LocationService } from '../../../services/location/location.service'
import { TrackingService } from '../../../services/tracking/tracking.service'
import { startWith } from 'rxjs'
import { LoadingService } from '../../../services/loadingService/loading.service'
import { Violence, ViolenceType } from '../../../models/violence.interface'
import { OptionSelect } from '../../search/search.component'
import { ViolenceService } from '../../../services/violence/violence.service'

@Component({
    selector: 'app-incident-form',
    standalone: true,
    imports: [
        MatStepperModule,
        FormsModule,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatError,
        MatIcon,
        MatIconModule,
        MatCheckboxModule,
        MatRadioModule,
        MatSelectModule,
        MatDatepickerModule,
        MatNativeDateModule,
        NgIf,
        NgFor,
        MatFormFieldModule,
        MatInputModule,
        MatAutocompleteModule,
        SearchSelectComponent,
        RouterLink,
    ],
    providers: [DatePipe],
    templateUrl: './incident-form.component.html',
    styleUrl: './incident-form.component.css',
})
export class IncidentFormComponent {
    selectedFile: File | null = null
    fileName: string = ''
    errorMsg: string[] | null = null
    selectedPerson: Outsider | null = null
    conflictRelated = conflictRelated
    typePerson = typePerson
    identificationTypes = identificationTypes
    sex = sex
    civilStatus = civilStatus
    sexualOrientation = sexualOrientation
    educationLevel = educationLevel
    lengueageProperty = lengueageProperty
    occupation = occupation
    leadertype = leadertype
    whoReports = whoReports
    disabilityTypes = disabilityTypes
    ethnic = ethnic
    insuranceKind = insuranceKind
    religion = religion
    eps = eps
    territories = territories
    organizations = organization
    relationship = relationship
    subjets = subjet
    reportedToOptions = reportedTo
    sourceIncome = sourceIncome
    violences = violence
    affectationKindIncident = affectationKindIncident
    incidentId: string | null = null
    incidentselected: Incident | null = null
    violenceSelected: OptionSelect[] = []
    private fromPage = 1
    locations: Location[] = []
    listViolences: Violence[] = []
    listViolencesType: ViolenceType[] = []
    displayFn = (location: Location) =>
        `${location?.city}, ${location?.department ?? ''}`
    idFn = (location: Location) => location.id

    constructor(
        private fb: FormBuilder,
        private incidentService: IncidentService,
        private toastr: ToastrService,
        private personService: PersonService,
        private router: Router,
        private route: ActivatedRoute,
        private dialog: MatDialog,
        private uploadfileService: UploadfileService,
        private locationService: LocationService,
        private datePipe: DatePipe,
        private trackingService: TrackingService,
        private loadingService: LoadingService,
        private serviceViolence: ViolenceService,
        private adapter: DateAdapter<Date>
    ) {
        this.adapter.setLocale('es-CO')
    }

    private fetchViolence() {
        this.serviceViolence.getViolenceType().subscribe((data) => {
            this.listViolencesType = data
            this.violenceSelected =
                this.serviceViolence.getOptionViolenceType(data)
        })
    }

    ngOnInit() {
        this.fetchViolence()

        // arrays base
        this.incidentForm.setControl(
            'victims',
            this.outsiderForm.get('victimas') as FormArray
        )

        this.incidentForm.setControl(
            'perpetrors',
            this.secondOutsiderForm.get('agresores') as FormArray
        )

        this.addVictim()
        this.addAgressor()

        this.incidentService.asyncErrorValidation$.subscribe((error) => {
            if (error) {
                this.errorMsg = FormUtils.parseErrorMsgs(error)
            }
        })

        const subj = this.incidentForm.get('complaint.subject')!
        const desc = this.incidentForm.get('complaint.description')!
        const reportedTo = this.incidentForm.get('complaint.reportedTo')!
        const takenMeasures = this.incidentForm.get('complaint.takenMeasures')!
        const registeredAt = this.incidentForm.get('complaint.registeredAt')!

        subj.valueChanges.pipe(startWith(subj.value)).subscribe((value) => {
            if (value === 'NO') {
                desc.enable({ emitEvent: false })
                desc.setValidators([Validators.required])
                ;[reportedTo, takenMeasures, registeredAt].forEach((ctrl) => {
                    ctrl.reset('', { emitEvent: false })
                    ctrl.clearValidators()
                    ctrl.disable({ emitEvent: false })
                })
            }

            if (value === 'SI') {
                ;[reportedTo, takenMeasures].forEach((ctrl) => {
                    ctrl.enable({ emitEvent: false })
                    ctrl.setValidators([Validators.required])
                })
                registeredAt.enable({ emitEvent: false })
                registeredAt.clearValidators()

                desc.reset('', { emitEvent: false })
                desc.clearValidators()
                desc.disable({ emitEvent: false })
            } else if (value === 'SIN INFORMACION') {
                ;[desc, reportedTo, takenMeasures, registeredAt].forEach(
                    (ctrl) => {
                        ctrl.reset('', { emitEvent: false })
                        ctrl.clearValidators()
                        ctrl.disable({ emitEvent: false })
                    }
                )
            }

            ;[desc, reportedTo, takenMeasures, registeredAt].forEach((ctrl) => {
                ctrl.updateValueAndValidity({ emitEvent: false })
            })
        })

        this.incidentForm
            .get('address.georeference')
            ?.valueChanges.subscribe((checked) => {
                const geoRefControl = this.incidentForm.get('address.geoRef')
                if (!(geoRefControl instanceof FormGroup)) {
                    return
                }
                const geoRef = geoRefControl as FormGroup
                const fields = ['latitude', 'longitude']
                fields.forEach((fieldName) => {
                    const control = geoRef.get(fieldName)
                    if (!checked) {
                        control?.setValue(null)
                        control?.clearValidators()
                        control?.disable()
                    } else {
                        control?.setValidators([Validators.required])
                        control?.enable()
                    }
                    control?.updateValueAndValidity()
                })
            })

        // Cargar ubicaciones
        this.locationService.allLocation().subscribe((data) => {
            this.locations = data

            const id = this.route.snapshot.paramMap.get('id')

            if (id) {
                this.incidentService.getIncidentId(id).subscribe((incident) => {
                    if (incident) {
                        this.incidentId = String(incident.id)
                        this.incidentselected = incident

                        // Buscar ubicación asociada
                        const matched = this.locations.find(
                            (loc) => loc.id === incident.location?.id
                        )

                        const dateTime = this.incidentService.setDateTimeToForm(
                            incident.date
                        )

                        //Cargar los campos del incidente
                        this.incidentForm.patchValue({
                            description: incident.description ?? '',
                            witnessRelationship:
                                incident.witnessRelationship ?? '',
                            affectationKind: incident.affectationKind ?? '',
                            date: dateTime.date,
                            time: dateTime.time,
                            conflictRelated: incident.conflictRelated ?? '',
                            phisicAgression: incident.phisicAgression ?? false,
                            address: incident.address ?? '',
                            location: matched ?? null,
                        })

                        // complaint
                        if (incident.complaint) {
                            const complaintGroup = this.incidentForm.get(
                                'complaint'
                            ) as FormGroup
                            complaintGroup.patchValue({
                                id: incident.complaint.id,
                                subject: incident.complaint.subject,
                                reportedTo: incident.complaint.reportedTo,
                                description: incident.complaint.description,
                                takenMeasures: incident.complaint.takenMeasures,
                                registeredAt:
                                    this.trackingService.toLocalDateOnly(
                                        incident.complaint.registeredAt
                                    ),
                            })
                        }

                        //Cargar víctimas
                        const victimasArray = this.outsiderForm.get(
                            'victimas'
                        ) as FormArray
                        victimasArray.clear()

                        incident.victims?.forEach((victim) => {
                            const victimForm = this.newVictim(true)

                            // Normalizar disability
                            let disability =
                                victim.socioeconomicStatus?.disability
                            if (typeof disability === 'string') {
                                try {
                                    disability = JSON.parse(disability)
                                } catch {
                                    disability = []
                                }
                            }

                            victimForm.patchValue({
                                ...victim,
                                birthDate: this.trackingService.toLocalDateOnly(
                                    victim.birthDate
                                ),
                                socioeconomicStatus: {
                                    ...victim.socioeconomicStatus,
                                    disability: disability ?? [],
                                },
                            })

                            // Calcular edad
                            victimForm
                                .get('birthDate')
                                ?.valueChanges.subscribe((date: Date) => {
                                    const age = this.calculateAge(date)
                                    victimForm.get('age')?.setValue(age)
                                })

                            victimasArray.push(victimForm)
                        })

                        //Cargar agresores
                        const agresoresArray = this.secondOutsiderForm.get(
                            'agresores'
                        ) as FormArray
                        agresoresArray.clear()

                        incident.perpetrors?.forEach((perp, index) => {
                            const agresorForm = this.newAgressor(true)
                            agresorForm.patchValue(perp)
                            agresoresArray.push(agresorForm)
                            this.fillAggressorFields(perp, index)
                        })

                        //Cargar metadata de agresores
                        const metadaData = this.perpetrorsMetadata
                        metadaData.clear()
                        ;(Array.isArray(incident.perpetrorsMetadata)
                            ? incident.perpetrorsMetadata
                            : []
                        ).forEach((meta: any) => {
                            const g = this.newPerpetratorMetadata()
                            g.patchValue({
                                id: meta?.id ?? null,
                                document: meta?.document ?? 0,
                                relationship: meta?.relationship ?? '',
                                organization: meta?.organization ?? '',
                            })
                            metadaData.push(g)
                        })

                        //Cargar tipos de violencia
                        const violenceArray = this.incidentForm.get(
                            'violence'
                        ) as FormArray
                        violenceArray.clear()
                        incident.violence?.forEach((violence) => {
                            const violenceGroup = this.fb.group({
                                name: [violence.name ?? ''],
                                violenceType: [
                                    (violence.violenceType ?? []).map(
                                        (vt) => vt.id
                                    ),
                                ],
                            })
                            violenceArray.push(violenceGroup)
                        })

                        //Cargar testigos
                        const testigosArray = this.incidentForm.get(
                            'witnesses'
                        ) as FormArray
                        const isEditing = true
                        testigosArray.clear()

                        incident.witnesses?.forEach((witness) => {
                            const witnessForm = this.fb.group({
                                identificationCode: [
                                    {
                                        value:
                                            witness.identificationCode ?? null,
                                        disabled: isEditing,
                                    },
                                ],
                                identificationType: [
                                    {
                                        value: witness.identificationType ?? '',
                                        disabled: isEditing,
                                    },
                                ],
                                name: [
                                    {
                                        value: witness.name ?? '',
                                        disabled: isEditing,
                                    },
                                ],
                                lastName: [
                                    {
                                        value: witness.lastName ?? '',
                                        disabled: isEditing,
                                    },
                                ],
                                phone: [
                                    {
                                        value: witness.phone ?? '',
                                        disabled: isEditing,
                                    },
                                ],
                            })
                            testigosArray.push(witnessForm)
                        })
                    }
                })
            }
        })
    }

    searchPerson = (value: string) => {
        return this.personService.searchPerson(value)
    }
    showPerson = (person: Outsider) =>
        `${person.identificationCode} - ${person.name} ${person.lastName}`
    personIdentifier = (person: Outsider) => person.identificationCode

    fillWitnessFields(person: Outsider, index: number) {
        const incidentForm = this.testigos.at(index) as FormGroup

        incidentForm.patchValue({
            name: person.name,
            lastName: person.lastName,
            identificationType: person.identificationType,
            personRef: person.personRef,
            phone: person.phone ?? '',
        })
    }

    incidentForm = this.fb.group({
        violence: this.fb.array([this.createViolenceGroup()]),
        conflictRelated: ['', []],
        phisicAgression: [false],
        description: ['', []],
        affectationKind: ['', []],
        witnessRelationship: ['', []],
        date: [null as Date | null],
        time: ['', []],
        location: [null as Location | null, []],
        address: this.fb.group({
            id: [null as number | null],
            street: ['', []],
            number: ['', []],
            complement: ['', []],
            territory: ['', []],
            authority: ['', []],
            territoryCode: [0, []],
            authorityCode: [0, []],
            georeference: [false, []],
            geoRef: this.fb.group({
                id: [null as number | null],
                latitude: [null as string | null],
                longitude: [null as string | null],
            }),
        }),
        perpetrors: this.fb.array([]),
        perpetrorsMetadata: this.fb.array([]),
        victims: this.fb.array([]),
        witnesses: this.fb.array([]),
        attachment: this.fb.control<File | null>(null),
        complaint: this.fb.group({
            id: [null as number | null],
            subject: ['', []],
            reportedTo: ['', []],
            description: ['', []],
            registeredAt: ['', []],
            takenMeasures: ['', []],
        }),
    })

    outsiderForm = this.fb.group({
        victimas: this.fb.array([]),
    })

    attachmentForm = this.fb.group({})

    fillFields(person: Outsider, index: number) {
        const victimForm = this.victimas.at(index) as FormGroup

        const birthDateLocal = this.trackingService.toLocalDateOnly(
            person.birthDate ?? null
        )

        victimForm.patchValue({
            name: person.name,
            lastName: person.lastName,
            identificationType: person.identificationType ?? '',
            personRef: person.personRef ?? '',
            civilianStatus: person.socioeconomicStatus?.civilianStatus ?? '',
            level: person.education?.level ?? '',
            culturalKnowledge: person.education?.culturalKnowledge ?? '',
            nasaYuwe: person.education?.nasaYuwe ?? '',
            isIlliterate: person.education?.isIlliterate ?? false,
            eps: person.socialSecurity?.eps ?? '',
            culturaEthnic: person.socioeconomicStatus?.culturaEthnic ?? '',
            qtyChildren: person.socioeconomicStatus?.qtyChildren ?? 0,
            religion: person.socioeconomicStatus?.religion ?? '',
            landArea: person.socioeconomicStatus?.landArea ?? 0,
            sex: person.sex ?? '',
            // placeBirth: person.placeBirth ?? '',
            phone: person.phone ?? '',
            birthDate: birthDateLocal,
            email: person.email ?? '',
        })
        victimForm
            .get('socialSecurity.eps')
            ?.setValue(person.socialSecurity?.eps ?? '')
        victimForm
            .get('socioeconomicStatus.gender')
            ?.setValue(person.socioeconomicStatus?.gender ?? '')
        victimForm
            .get('socioeconomicStatus.culturaEthnic')
            ?.setValue(person.socioeconomicStatus?.culturaEthnic ?? '')
    }
    secondOutsiderForm = this.fb.group({
        agresores: this.fb.array([]),
    })

    fillAggressorFields(person: Outsider, index: number) {
        const agresorForm = this.agresores.at(index) as FormGroup

        agresorForm.patchValue({
            name: person.name,
            lastName: person.lastName,
            identificationType: person.identificationType,
            personRef: person.personRef,
            phone: person.phone ?? '',
        })
        const metaGroup = this.perpetrorsMetadata.at(index) as FormGroup
        metaGroup?.patchValue({
            document: person.identificationCode || null,
        })
    }

    get victimas(): FormArray {
        return this.outsiderForm.get('victimas') as FormArray
    }
    get agresores(): FormArray {
        return this.secondOutsiderForm.get('agresores') as FormArray
    }

    get perpetrorsMetadata(): FormArray<FormGroup> {
        return this.incidentForm.get(
            'perpetrorsMetadata'
        ) as FormArray<FormGroup>
    }

    get testigos(): FormArray {
        return this.incidentForm.get('witnesses') as FormArray
    }

    newVictim(isEditing: boolean = false): FormGroup {
        return this.fb.group({
            id: [null],
            personRef: [{ value: '', disabled: isEditing }],
            identificationCode: [{ value: null, disabled: isEditing }],
            identificationType: [{ value: '', disabled: isEditing }],
            name: [{ value: '', disabled: isEditing }],
            lastName: [{ value: '', disabled: isEditing }],
            // location: [{ value: null as Location | null, disabled: isEditing }],
            socialSecurity: this.fb.group({
                id: [null],
                hasInsurance: [{ value: false, disabled: isEditing }],
                eps: [{ value: '', disabled: isEditing }],
                insuranceKind: [{ value: '', disabled: isEditing }],
            }),
            education: this.fb.group({
                id: [null],
                level: [{ value: '', disabled: isEditing }],
                culturalKnowledge: [{ value: '', disabled: isEditing }],
                nasaYuwe: [{ value: '', disabled: isEditing }],
                isIlliterate: [{ value: false, disabled: isEditing }],
            }),
            birthDate: [{ value: '', disabled: isEditing }],
            placeBirth: [
                { value: null as Location | null, disabled: isEditing },
            ],
            age: [{ value: 0, disabled: isEditing }],
            sex: [{ value: '', disabled: isEditing }],
            phone: [{ value: '', disabled: isEditing }],
            email: [
                { value: '', disabled: isEditing },
                [
                    Validators.pattern(
                        /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                    ),
                ],
            ],
            profesion: this.fb.group({
                id: [null],
                companyName: [{ value: '', disabled: isEditing }],
                leaderRole: [{ value: '', disabled: isEditing }],
                name: [{ value: '', disabled: isEditing }],
                isLeader: [{ value: false, disabled: isEditing }],
            }),
            socioeconomicStatus: this.fb.group({
                id: [null],
                isHeadOfHousehold: [{ value: false, disabled: isEditing }],
                qtyChildren: [{ value: 0, disabled: isEditing }],
                religion: [{ value: '', disabled: isEditing }],
                landArea: [{ value: 0, disabled: isEditing }],
                isConflictVictim: [{ value: false, disabled: isEditing }],
                hasProtectionScheme: [{ value: false, disabled: isEditing }],
                civilianStatus: [{ value: '', disabled: isEditing }],
                gender: [{ value: '', disabled: isEditing }],
                culturaEthnic: [{ value: '', disabled: isEditing }],
                sourceIncome: [{ value: '', disabled: isEditing }],
                sourceIncomeMethod: [{ value: '', disabled: isEditing }],
                ownsLand: [{ value: false, disabled: isEditing }],
                isLandOwner: [{ value: false, disabled: isEditing }],
                disability: [{ value: [], disabled: isEditing }],
                placeWork: [
                    { value: null as Location | null, disabled: isEditing },
                ],
            }),
            address: this.fb.group({
                id: [null as number | null],
                street: [{ value: '', disabled: isEditing }],
                number: [{ value: '', disabled: isEditing }],
                complement: ['', []],
                territory: [{ value: '', disabled: isEditing }],
                authority: [{ value: '', disabled: isEditing }],
                territoryCode: [{ value: 0, disabled: isEditing }],
                authorityCode: [{ value: 0, disabled: isEditing }],
            }),
        })
    }

    newAgressor(isEditing: boolean = false): FormGroup {
        return this.fb.group({
            id: [null],
            personRef: [{ value: '', disabled: isEditing }],
            identificationCode: [{ value: null, disabled: isEditing }],
            identificationType: [{ value: '', disabled: isEditing }],
            name: [{ value: '', disabled: isEditing }],
            lastName: [{ value: '', disabled: isEditing }],
            phone: [{ value: '', disabled: isEditing }],
            sex: [{ value: '', disabled: isEditing }],

            address: this.fb.group({
                id: [null],
                street: [{ value: '', disabled: isEditing }],
                number: [{ value: '', disabled: isEditing }],
                complement: [{ value: '', disabled: isEditing }],
                // territoryCode: [{ value: 0, disabled: isEditing }],
                // territory: [{ value: '', disabled: isEditing }],
                // authorityCode: [{ value: 0, disabled: isEditing }],
                // authority: [{ value: '', disabled: isEditing }],
            }),
        })
    }

    newPerpetratorMetadata(): FormGroup {
        return this.fb.group({
            id: [null],
            document: [0, []],
            relationship: ['', []],
            organization: ['', []],
        })
    }
    calculateAge(dateBirth: Date): number | null {
        if (!dateBirth) return null

        const today = new Date()
        const birth = new Date(dateBirth)
        let age = today.getFullYear() - birth.getFullYear()
        const month = today.getMonth() - birth.getMonth()

        if (month < 0 || (month === 0 && today.getDate() < birth.getDate())) {
            age--
        }

        return age
    }

    addVictim() {
        const victimForm = this.newVictim()
        victimForm.get('birthDate')?.valueChanges.subscribe((date: Date) => {
            const age = this.calculateAge(date)
            victimForm.get('age')?.setValue(age)
        })

        this.victimas.push(victimForm)
    }

    deleteVictim(index: number) {
        this.victimas.removeAt(index)
    }

    addAgressor() {
        this.agresores.push(this.newAgressor())
        this.perpetrorsMetadata.push(this.newPerpetratorMetadata())
    }

    deleteAgressor(index: number) {
        ;(this.incidentForm.get('perpetrors') as FormArray).removeAt(index)
        this.perpetrorsMetadata.removeAt(index)
    }

    openUploadModal(): void {
        const dialogRef = this.dialog.open(SelectFileComponent, {
            width: 'auto',
            height: 'auto',
            maxHeight: '80vh',
        })

        dialogRef.afterClosed().subscribe((file: File | null | undefined) => {
            if (file instanceof File) {
                this.selectedFile = file
                this.incidentForm.patchValue({ attachment: file })
                this.incidentForm.get('attachment')?.markAsDirty()
                this.incidentForm.get('attachment')?.updateValueAndValidity()
            }
            if (file === null) {
                this.clearSelectedFile()
            }
        })
    }

    clearSelectedFile(): void {
        this.selectedFile = null
        this.incidentForm.patchValue({ attachment: null })
        this.incidentForm.get('attachment')?.markAsDirty()
        this.incidentForm.get('attachment')?.updateValueAndValidity()
    }

    get violence(): FormArray {
        return this.incidentForm.get('violence') as FormArray
    }

    createViolenceGroup(): FormGroup {
        return this.fb.group({
            name: ['', Validators.required],
            violenceType: [[]],
        })
    }

    addViolence(): void {
        this.violence.push(this.createViolenceGroup())
    }

    removeViolence(index: number): void {
        this.violence.removeAt(index)
    }

    getViolenceTypesForIndex(index: number): ViolenceType[] {
        const group = this.violence.at(index) as FormGroup
        const selectedCategory = group.get('name')?.value as string | null

        if (!selectedCategory) return []

        const category = selectedCategory.trim().toUpperCase()

        return (this.listViolencesType ?? []).filter(
            (t) => (t.category ?? '').trim().toUpperCase() === category
        )
    }

    onChangeViolence(index: number): void {
        const group = this.violence.at(index) as FormGroup
        group.get('violenceType')?.reset([], { emitEvent: false })
    }

    get witnesses(): FormArray {
        return this.incidentForm.get('witnesses') as FormArray
    }

    addWitness() {
        const witnessFormGroup = this.fb.group({
            id: [null],
            personRef: ['', []],
            identificationCode: [null, []],
            identificationType: ['', []],
            name: ['', []],
            lastName: ['', []],
            phone: ['', []],
        })
        this.witnesses.push(witnessFormGroup)
    }

    removeWitness(index: number) {
        this.witnesses.removeAt(index)
    }

    buildDateTime(raw: any): string | null {
        if (!raw.date) return null
        const date = new Date(raw.date)
        if (raw.time) {
            const [hours, minutes] = raw.time.split(':')
            date.setHours(+hours)
            date.setMinutes(+minutes)
        } else {
            date.setHours(0)
            date.setMinutes(0)
        }
        date.setSeconds(0)
        return this.datePipe.transform(date, 'yyyy-MM-ddTHH:mm:ss')!
    }

    saveAll() {
        if (this.incidentForm.invalid) {
            this.toastr.error('Faltan campos por llenar.')
            return
        }
        const raw = this.incidentForm.getRawValue()
        const formattedVictims = (raw.victims || [])
            .filter((victim: any) =>
                this.incidentService.hasValidValues(victim)
            )
            .map((victim: any) => {
                const disabilityValue =
                    victim.socioeconomicStatus?.disability || []
                return {
                    ...victim,
                    birthDate: victim.birthDate
                        ? this.datePipe.transform(
                              victim.birthDate,
                              'yyyy-MM-dd'
                          )
                        : null,
                    socioeconomicStatus: {
                        ...victim.socioeconomicStatus,
                        disability: JSON.stringify(disabilityValue),
                    },
                }
            })

        const formattedComplaint = {
            ...raw.complaint,
            registeredAt: raw.complaint.registeredAt
                ? this.datePipe.transform(
                      raw.complaint.registeredAt,
                      'yyyy-MM-dd'
                  )
                : null,
            id: raw.complaint?.id ?? null,
        }

        const filteredPerpetrors = (raw.perpetrors || []).filter((p: any) =>
            this.incidentService.hasValidValues(p)
        )

        //Filtrar metadatos
        const filteredPerpetrorsMetadata = (
            raw.perpetrorsMetadata || []
        ).filter((m: any) => this.incidentService.hasValidValues(m))

        //Filtrar testigos
        const filteredWitnesses = (raw.witnesses || []).filter((w: any) =>
            this.incidentService.hasValidValues(w)
        )

        const filteredViolence = (raw.violence || []).filter((w: any) =>
            this.incidentService.hasValidValues(w)
        )

        // Payload
        let payload: any = {
            // violenceType: violenceTypeString,
            violence: filteredViolence,
            description: raw.description ?? '',
            witnessRelationship: raw.witnessRelationship ?? '',
            affectationKind: raw.affectationKind ?? '',
            conflictRelated: raw.conflictRelated ?? '',
            phisicAgression: raw.phisicAgression ?? false,
            date: this.buildDateTime(raw),
            location: raw.location,
            address: raw.address,
            victims: formattedVictims,
            perpetrors: filteredPerpetrors,
            witnesses: filteredWitnesses,
            perpetrorsMetadata: filteredPerpetrorsMetadata,
            complaint: formattedComplaint,
            ...(this.incidentId != null ? { id: Number(this.incidentId) } : {}),
        }

        // Quitar null/undefined en objetos anidados
        payload = this.incidentService.stripNullIds(payload)

        // FormData
        const formData = new FormData()
        formData.append('payload', JSON.stringify(payload))

        if (raw.attachment) {
            formData.append('file', raw.attachment, raw.attachment.name)
        }

        // Crear o editar
        this.loadingService.show(true)
        const request$ = this.incidentId
            ? this.incidentService.updateIncident(
                  Number(this.incidentId),
                  formData
              )
            : this.incidentService.createIncident(formData)

        request$.subscribe({
            next: () => {
                this.loadingService.show(false)
                this.toastr.success(
                    this.incidentId
                        ? 'El incidente fue editado correctamente!'
                        : 'El incidente fue creado correctamente!',
                    'Exitoso!',
                    { progressBar: true, timeOut: 5000 }
                )
                this.router.navigate(['list-incident/'], {
                    queryParams: { page: this.fromPage },
                })
            },
            error: (err: HttpErrorResponse) => {
                this.loadingService.show(false)

                const backendErrors = err.error
                const message =
                    this.incidentService.formatBackendErrors(backendErrors)

                this.toastr.error(
                    message || 'Error desconocido en el servidor.',
                    'Error',
                    {
                        progressBar: true,
                        timeOut: 6000,
                        enableHtml: true,
                    }
                )
            },
        })
    }

    openFile(incident: Incident): void {
        const id = incident?.fileAttach?.fileUrl
        const name = incident?.fileAttach?.fileName || 'archivo.pdf'
        if (!id) return
        this.uploadfileService.openPdfById(id, name).subscribe({
            error: () => {
                const msg = `No se logro descargar o previsualizar el archivo`
                this.toastr.error(msg, 'Error', {
                    progressBar: true,
                    timeOut: 5000,
                })
            },
        })
    }

    openIncidentPdf(): void {
        const id = this.incidentselected?.fileAttach?.fileUrl
        const name =
            this.incidentselected?.fileAttach?.fileName || 'archivo.pdf'
        if (!id) return
        this.uploadfileService.openPdfById(id, name).subscribe({
            error: () => {
                const msg = `No se logro descargar o previsualizar el archivo`
                this.toastr.error(msg, 'Error', {
                    progressBar: true,
                    timeOut: 5000,
                })
            },
        })
    }

    openSelectedPdf(): void {
        if (!this.selectedFile) return
        if (this.selectedFile.type !== 'application/pdf') {
            const msg = `Solo podemos mostrarte archivos PDF`
            this.toastr.warning(msg, 'Aviso')
            return
        }
        this.uploadfileService.openLocalPdf(
            this.selectedFile,
            this.selectedFile.name
        )
    }

    hasFile(incident: Incident) {
        return (
            !!incident?.fileAttach?.fileUrl && !!incident?.fileAttach?.fileName
        )
    }

    isViolenceSelected(violenceName: string, index: number): boolean {
        if (!violenceName) {
            return false
        }

        const violenceArray = this.violence
        for (let i = 0; i < violenceArray.length; i++) {
            if (i !== index) {
                const group = violenceArray.at(i) as FormGroup
                const name = group.get('name')?.value
                if (name === violenceName) {
                    return true
                }
            }
        }
        return false
    }
}
